import React, { useEffect, useMemo, useState } from 'react';

/**
 * Generic Geodezija tracking screen for non-GPS assets.
 *
 * Uses FastAPI endpoints under /gps/* so Nginx route `/gps/* -> :8005` works in production.
 */
const GeodezijaAssetTracking = ({ onBack, user, assetType }) => {
  const API_URL = process.env.REACT_APP_PYTHON_API_URL || '';

  const config = useMemo(() => {
    const common = {
      employeesPath: '/zaposlenici-aktivni'
    };
    switch (assetType) {
      case 'skener':
        return {
          ...common,
          title: 'Skener',
          listPath: '/gps/skener',
          idKey: 'skener_id',
          primary: (d) => d.evidencijski_broj ?? '',
          secondary: (d) => d.model ?? '',
          fields: [
            { label: 'Evidencijski broj', keys: ['evidencijski_broj'] },
            { label: 'Model', keys: ['model'] },
            { label: 'Serijski broj', keys: ['serijski_broj'] },
            { label: 'Inventarski broj', keys: ['inventarski_broj'] },
            { label: 'Dodatna oprema', keys: ['detalji.dodatna_oprema', 'dodatna_oprema', 'dodatna_oprema_nova'] },
            { label: 'Napomena', keys: ['detalji.napomena', 'napomena', 'napomena_nova'] },
            { label: 'Oštećenje', keys: ['detalji.ostecenje', 'ostecenje', 'ostecenje_novo'] }
          ],
          detailsPath: (id) => `/gps/skener/${id}`,
          statusPath: (id) => `/gps/skener/${id}/status`,
          transferPath: (id) => `/gps/skener/${id}/transfer`,
          returnPath: (id) => `/gps/skener/${id}/return`
        };
      case 'dron':
        return {
          ...common,
          title: 'Dron',
          listPath: '/gps/dron',
          idKey: 'dron_id',
          primary: (d) => d.identifikacijska_oznaka ?? '',
          secondary: (d) => d.model ?? '',
          fields: [
            { label: 'Oznaka', keys: ['identifikacijska_oznaka'] },
            { label: 'Model', keys: ['model'] },
            { label: 'Serijski broj', keys: ['serijski_broj'] },
            { label: 'Broj dozvole', keys: ['broj_dozvole'] },
            { label: 'Ovlašteni rukovatelj', keys: ['ovlasteni_rukovatelj'] },
            { label: 'Dodatna oprema', keys: ['detalji.dodatna_oprema', 'dodatna_oprema', 'dodatna_oprema_nova'] },
            { label: 'Napomena', keys: ['detalji.napomena', 'napomena', 'napomena_nova'] },
            { label: 'Oštećenje', keys: ['detalji.ostecenje', 'ostecenje', 'ostecenje_novo'] }
          ],
          detailsPath: (id) => `/gps/dron/${id}`,
          statusPath: (id) => `/gps/dron/${id}/status`,
          transferPath: (id) => `/gps/dron/${id}/transfer`,
          returnPath: (id) => `/gps/dron/${id}/return`
        };
      case 'nivelir':
        return {
          ...common,
          title: 'Nivelir',
          listPath: '/gps/nivelir',
          idKey: 'nivelir_id',
          primary: (d) => d.evidencijski_broj ?? (d.display_text ?? ''),
          secondary: (d) => d.model ?? '',
          fields: [
            { label: 'Evidencijski broj', keys: ['evidencijski_broj'] },
            { label: 'Model', keys: ['model'] },
            { label: 'Serijski broj', keys: ['serijski_broj'] },
            { label: 'Inventarski broj', keys: ['inventarski_broj'] },
            { label: 'Dodatna oprema', keys: ['detalji.dodatna_oprema', 'dodatna_oprema', 'dodatna_oprema_nova'] },
            { label: 'Napomena', keys: ['detalji.napomena', 'napomena', 'napomena_nova'] },
            { label: 'Oštećenje', keys: ['detalji.ostecenje', 'ostecenje', 'ostecenje_novo'] }
          ],
          detailsPath: (id) => `/gps/nivelir/${id}`,
          statusPath: (id) => `/gps/nivelir/${id}/status`,
          transferPath: (id) => `/gps/nivelir/${id}/transfer`,
          returnPath: (id) => `/gps/nivelir/${id}/return`
        };
      case 'stanica':
        return {
          ...common,
          title: 'Stanica',
          listPath: '/gps/stanice',
          idKey: 'stanica_id',
          primary: (d) => d.display_text ?? d.evidencijski_broj ?? '',
          secondary: (d) => d.uredaj ?? '',
          fields: [
            { label: 'Evidencijski broj', keys: ['evidencijski_broj'] },
            { label: 'Uređaj', keys: ['uredaj'] },
            { label: 'Serijski broj', keys: ['serijski_broj'] },
            { label: 'Inventarski broj', keys: ['inventarski_broj'] },
            { label: 'Dodatna oprema', keys: ['detalji.dodatna_oprema', 'dodatna_oprema', 'dodatna_oprema_nova'] },
            { label: 'Napomena', keys: ['detalji.napomena', 'napomena', 'napomena_nova'] },
            { label: 'Oštećenje', keys: ['detalji.ostecenje', 'ostecenje', 'ostecenje_novo'] }
          ],
          detailsPath: (id) => `/gps/stanica/${id}`,
          statusPath: (id) => `/gps/stanica/${id}/status`,
          transferPath: (id) => `/gps/stanica/${id}/transfer`,
          returnPath: (id) => `/gps/stanica/${id}/return`
        };
      case 'tableti':
        return {
          ...common,
          title: 'Tableti',
          listPath: '/gps/tableti',
          idKey: 'tablet_id',
          primary: (d) => d.display_text ?? d.evidencijski_broj ?? d.inventarski_broj ?? `${d.tablet_id ?? ''}`,
          secondary: (d) => d.model ?? d.serijski_broj ?? '',
          fields: [
            { label: 'Evidencijski broj', keys: ['evidencijski_broj'] },
            { label: 'Inventarski broj', keys: ['inventarski_broj'] },
            { label: 'Model', keys: ['model'] },
            { label: 'Serijski broj', keys: ['serijski_broj'] },
            { label: 'Dodatna oprema', keys: ['detalji.dodatna_oprema', 'dodatna_oprema', 'dodatna_oprema_nova'] },
            { label: 'Napomena', keys: ['detalji.napomena', 'napomena', 'napomena_nova'] },
            { label: 'Oštećenje', keys: ['detalji.ostecenje', 'ostecenje', 'ostecenje_novo'] }
          ],
          detailsPath: (id) => `/gps/tablet/${id}`,
          statusPath: (id) => `/gps/tablet/${id}/status`,
          transferPath: (id) => `/gps/tablet/${id}/transfer`,
          returnPath: (id) => `/gps/tablet/${id}/return`
        };
      default:
        return null;
    }
  }, [assetType]);

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [status, setStatus] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionType, setActionType] = useState(null); // 'forward'
  const [forwardPrefilled, setForwardPrefilled] = useState(false);

  const [form, setForm] = useState({
    forward_destination: 'office', // 'office' | 'employee'
    to_worker_id: '',
    dodatna_oprema: '',
    napomena: '',
    ostecenje: ''
  });

  const zaposlenikId = user?.zaposlenik_id;
  const statusStanje = (status?.stanje_ured ?? '').toString().trim().toLowerCase();
  const statusIdNum = status?.status_id == null ? null : Number(status.status_id);
  // Same as GPS: Proslijedi only when status is loaded and device is outside office (vani, status 1 or 2)
  const canAct = !!selected && status != null && statusStanje === 'vani' && (statusIdNum === 1 || statusIdNum === 2);
  const isAtOffice = status != null && (status?.status === 'at_office' || statusStanje === 'unutra');

  const getByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
  };

  const getFirstValue = (keys) => {
    for (const k of keys || []) {
      const v = getByPath(details, k);
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
      const v2 = getByPath(selected, k);
      if (v2 !== undefined && v2 !== null && String(v2).trim() !== '') return v2;
    }
    return '';
  };

  useEffect(() => {
    if (!config) return;
    fetchList();
    fetchEmployees();
  }, [config, zaposlenikId]);

  useEffect(() => {
    if (!selected || !config) return;
    setDetails(null);
    setStatus(null);
    setForwardPrefilled(false);
    fetchDetails(selected[config.idKey]);
    fetchStatus(selected[config.idKey]);
  }, [selected, config]);

  // When details are refetched (e.g. on open Proslijedi), allow prefill to run again with new DB values
  useEffect(() => {
    if (actionType === 'forward' && details) setForwardPrefilled(false);
  }, [actionType, details]);

  // Prefill forward form fields from details (from DB) when Proslijedi form is open
  useEffect(() => {
    if (actionType !== 'forward') {
      setForwardPrefilled(false);
      return;
    }
    if (forwardPrefilled) return;
    const dodatna_oprema =
      details?.detalji?.dodatna_oprema ?? details?.dodatna_oprema ?? selected?.dodatna_oprema ?? '';
    const napomena =
      details?.detalji?.napomena ?? details?.napomena ?? selected?.napomena ?? '';
    const ostecenje =
      details?.detalji?.ostecenje ?? details?.ostecenje ?? selected?.ostecenje ?? '';

    setForm((p) => ({
      ...p,
      dodatna_oprema,
      napomena,
      ostecenje
    }));
    setForwardPrefilled(true);
  }, [actionType, details, selected, forwardPrefilled]);

  const openForwardForm = () => {
    setError('');
    setSuccess('');
    setActionType('forward');
    setForwardPrefilled(false);
    if (selected && config) {
      fetchDetails(selected[config.idKey]);
    }
    setForm((p) => ({
      ...p,
      forward_destination: 'office',
      to_worker_id: '',
      dodatna_oprema: details?.detalji?.dodatna_oprema || details?.dodatna_oprema || selected?.dodatna_oprema || p.dodatna_oprema || '',
      napomena: details?.detalji?.napomena || details?.napomena || selected?.napomena || p.napomena || '',
      ostecenje: details?.detalji?.ostecenje || details?.ostecenje || selected?.ostecenje || p.ostecenje || ''
    }));
  };

  const fetchList = async () => {
    if (!config) return;
    try {
      setLoading(true);
      setError('');
      const qs = zaposlenikId ? `?zaposlenik_id=${encodeURIComponent(zaposlenikId)}` : '';
      const res = await fetch(`${API_URL}${config.listPath}${qs}`);
      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        throw new Error(`Failed to fetch list: HTTP ${res.status} ${res.statusText}${bodyText ? ` - ${bodyText}` : ''}`);
      }
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    if (!config) return;
    try {
      const res = await fetch(`${API_URL}${config.employeesPath}`);
      if (!res.ok) return;
      const data = await res.json();
      const sorted = (data || []).sort((a, b) => {
        if (a.prezime !== b.prezime) return (a.prezime || '').localeCompare(b.prezime || '', 'hr');
        return (a.ime || '').localeCompare(b.ime || '', 'hr');
      });
      setEmployees(sorted);
    } catch (_) {}
  };

  const fetchDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}${config.detailsPath(id)}`);
      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        throw new Error(`Failed to fetch details: HTTP ${res.status} ${res.statusText}${bodyText ? ` - ${bodyText}` : ''}`);
      }
      const data = await res.json();
      setDetails(data);
    } catch (e) {
      setError(e.message);
    }
  };

  const fetchStatus = async (id) => {
    try {
      const res = await fetch(`${API_URL}${config.statusPath(id)}`);
      if (res.status === 404) {
        setStatus(null);
        return;
      }
      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        throw new Error(`Failed to fetch device status: HTTP ${res.status} ${res.statusText}${bodyText ? ` - ${bodyText}` : ''}`);
      }
      const data = await res.json();
      setStatus(data);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleForward = async () => {
    if (!selected) return;
    if (!zaposlenikId) {
      setError('Nedostaje prijavljeni korisnik (zaposlenik_id).');
      return;
    }
    const dest = form.forward_destination;
    if (dest !== 'office' && dest !== 'employee') {
      setError('Molimo odaberite odredište (Ured ili Drugom zaposleniku).');
      return;
    }
    if (dest === 'employee') {
      if (!form.to_worker_id) {
        setError('Molimo odaberite zaposlenika');
        return;
      }
      if (parseInt(form.to_worker_id, 10) === parseInt(zaposlenikId, 10)) {
        setError('Ne možete proslijediti sami sebi.');
        return;
      }
    }
    try {
      setLoading(true);
      setError('');
      const id = selected[config.idKey];
      const endpoint = dest === 'office' ? config.returnPath(id) : config.transferPath(id);

      const payload = dest === 'office'
        ? {
            returning_worker_id: parseInt(zaposlenikId, 10),
            od_zaposlenik_id: parseInt(zaposlenikId, 10),
            dodatna_oprema: form.dodatna_oprema,
            napomena: form.napomena,
            ostecenje: form.ostecenje
          }
        : {
            to_worker_id: parseInt(form.to_worker_id, 10),
            od_zaposlenik_id: parseInt(zaposlenikId, 10),
            issuing_worker_id: parseInt(zaposlenikId, 10),
            dodatna_oprema: form.dodatna_oprema,
            napomena: form.napomena,
            ostecenje: form.ostecenje
          };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        const msg = Array.isArray(detail) ? detail.map((d) => d.msg || d).join(', ') : (detail || (dest === 'office' ? 'Greška pri vraćanju u ured' : 'Greška pri prosljeđivanju'));
        throw new Error(msg);
      }

      setSuccess(dest === 'office' ? `${config.title} uspješno vraćen u ured` : `${config.title} uspješno proslijeđen zaposleniku`);
      setActionType(null);
      setForm({ forward_destination: 'office', to_worker_id: '', dodatna_oprema: '', napomena: '', ostecenje: '' });
      setSelected(null);
      setDetails(null);
      setStatus(null);
      fetchList();
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!config) {
    return (
      <div style={{ padding: 20 }}>
        <button onClick={onBack}>Nazad</button>
        <div style={{ marginTop: 10 }}>Nepodržan tip uređaja.</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #CAE0BC 0%, #EAFAEA 100%)',
      fontFamily: 'Arial, sans-serif',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <button
            onClick={onBack}
            style={{
              padding: '10px 15px',
              background: '#6C8B58',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Nazad
          </button>
          <h1 style={{ margin: 0, color: '#6C8B58' }}>{config.title}</h1>
          <div style={{ width: 60 }} />
        </div>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, marginBottom: 15 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 15 }}>
            {success}
          </div>
        )}

        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 300, background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <h2 style={{ marginTop: 0, color: '#6C8B58' }}>Uređaji</h2>
            {loading && items.length === 0 ? (
              <div>Učitavanje...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {items.map((it) => (
                  <button
                    key={it[config.idKey]}
                    onClick={() => setSelected(it)}
                    style={{
                      padding: 12,
                      border: '2px solid',
                      borderColor: selected?.[config.idKey] === it[config.idKey] ? '#6C8B58' : '#e1e5e9',
                      borderRadius: 8,
                      background: selected?.[config.idKey] === it[config.idKey] ? '#f1f8f4' : 'white',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{ fontWeight: 'bold', color: '#333' }}>{config.primary(it) || '(bez oznake)'}</div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>{config.secondary(it) || ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div style={{ flex: 2, minWidth: 420, background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, color: '#6C8B58' }}>{config.primary(selected)}</h2>

              <div style={{
                padding: 15,
                background: statusStanje === 'unutra' ? '#e8f5e9' : (statusStanje === 'vani' ? '#fff3e0' : '#f8f9fa'),
                borderRadius: 8,
                marginBottom: 15,
                border: `2px solid ${statusStanje === 'unutra' ? '#4caf50' : (statusStanje === 'vani' ? '#ff9800' : '#e1e5e9')}`
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  Status: {statusStanje === 'unutra' ? 'U uredu' : (statusStanje === 'vani' ? 'Na terenu' : (status ? 'Nepoznato' : 'Učitavanje...'))}
                </div>
                {status ? (
                  <>
                    {status.current_holder_name && <div><strong>Trenutno kod:</strong> {status.current_holder_name}</div>}
                    {status.issued_at && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: 5 }}>Izdano: {new Date(status.issued_at).toLocaleString('hr-HR')}</div>}
                    {status.reserved_for_name && <div><strong>Rezervirano za:</strong> {status.reserved_for_name}</div>}
                  </>
                ) : (
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    Učitavanje statusa...
                  </div>
                )}
              </div>

              {isAtOffice && (
                <div style={{
                  padding: 10,
                  background: '#fff9c4',
                  borderRadius: 8,
                  marginBottom: 15,
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  ℹ️ Uređaj je u uredu. Za izdavanje iz ureda koristite desktop aplikaciju.
                </div>
              )}

              <div style={{ padding: 12, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e1e5e9', marginBottom: 15 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 10 }}>Detalji</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {(config.fields || []).map((f) => (
                    <div key={f.label} style={{ gridColumn: (f.label === 'Napomena' || f.label === 'Oštećenje') ? '1 / -1' : 'auto' }}>
                      <div style={{ fontSize: '0.85rem', color: '#666', fontWeight: 'bold', marginBottom: 4 }}>{f.label}</div>
                      <div style={{
                        padding: 8,
                        background: 'white',
                        borderRadius: 6,
                        border: '1px solid #ddd',
                        minHeight: f.label === 'Napomena' || f.label === 'Oštećenje' ? 60 : 20,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {String(getFirstValue(f.keys) || 'N/A')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <button
                  onClick={openForwardForm}
                  disabled={!canAct}
                  style={{
                    padding: '12px 16px',
                    background: canAct ? '#6C8B58' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: canAct ? 'pointer' : 'not-allowed',
                    fontWeight: 'bold'
                  }}
                >
                  Proslijedi
                </button>
              </div>
              {selected && !status && !error && (
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: 10 }}>
                  Učitavanje statusa... (Proslijedi će biti omogućen kada je uređaj na terenu)
                </div>
              )}

              {actionType === 'forward' && (
                <div style={{ padding: 15, background: '#f8f9fa', borderRadius: 8, marginTop: 10 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 10 }}>Proslijedi</div>
                  <div style={{
                    padding: 10,
                    background: '#e3f2fd',
                    borderRadius: 8,
                    marginBottom: 15,
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    ℹ️ Odaberi želite li proslijediti u ured ili drugom zaposleniku. U oba slučaja možete ažurirati napomenu, dodatnu opremu i oštećenje.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label>Odredište:</label>
                      <select
                        value={form.forward_destination}
                        onChange={(e) => setForm((p) => ({ ...p, forward_destination: e.target.value }))}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                      >
                        <option value="office">Ured</option>
                        <option value="employee">Drugom zaposleniku</option>
                      </select>
                    </div>

                    {form.forward_destination === 'employee' && (
                      <div>
                        <label>Zaposlenik:</label>
                        <select
                          value={form.to_worker_id}
                          onChange={(e) => setForm((p) => ({ ...p, to_worker_id: e.target.value }))}
                          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                        >
                          <option value="">Odaberi...</option>
                          {employees
                            .filter((emp) => emp.zaposlenik_id !== user?.zaposlenik_id)
                            .map((emp) => (
                              <option key={emp.zaposlenik_id} value={emp.zaposlenik_id}>
                                {emp.prezime} {emp.ime}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label>Dodatna oprema:</label>
                      <input
                        type="text"
                        value={form.dodatna_oprema}
                        onChange={(e) => setForm((p) => ({ ...p, dodatna_oprema: e.target.value }))}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                      />
                    </div>
                    <div>
                      <label>Oštećenje:</label>
                      <textarea
                        value={form.ostecenje}
                        onChange={(e) => setForm((p) => ({ ...p, ostecenje: e.target.value }))}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', minHeight: 80 }}
                      />
                    </div>
                    <div>
                      <label>Napomena:</label>
                      <textarea
                        value={form.napomena}
                        onChange={(e) => setForm((p) => ({ ...p, napomena: e.target.value }))}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', minHeight: 80 }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={handleForward} disabled={loading} style={{ padding: '10px 16px', background: '#6C8B58', color: 'white', border: 'none', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' }}>
                        {loading ? 'Spremanje...' : 'Potvrdi'}
                      </button>
                      <button onClick={() => setActionType(null)} style={{ padding: '10px 16px', background: '#ccc', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                        Odustani
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeodezijaAssetTracking;

